import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    ITelemetryPoint,
    ITelemetrySummary,
    IFuelEvent,
    IDailyMileage,
    IDailyFuelConsumption,
    IJournalResponse,
    IDailyMovement,
} from "../../../models/IMachineryTelemetry";
import {ITelemetryDashboard} from "../api";
import {fetchTelemetryDashboard, fetchLatestTelemetry} from "./actions";

// ── State ───────────────────────────────────────────────────────────────────

interface IMachineryTelemetryState {
    // данные
    fuelHistory: ITelemetryPoint[];
    voltageHistory: ITelemetryPoint[];
    engineHistory: ITelemetryPoint[];
    summary: ITelemetrySummary | null;
    latest: ITelemetryPoint | null;
    fuelEvents: IFuelEvent[];
    dailyMileage: IDailyMileage[];
    dailyFuelConsumption: IDailyFuelConsumption[];
    journal: IJournalResponse | null;
    dailyMovement: IDailyMovement[];

    // loading-стейты разделены: dashboard и latest загружаются независимо
    dashboardLoading: boolean;
    latestLoading: boolean;
    error: string | null;

    // stale = данные очищены, ждём новых (закрывает гэп между clear и pending)
    stale: boolean;
}

const initialState: IMachineryTelemetryState = {
    fuelHistory: [],
    voltageHistory: [],
    engineHistory: [],
    summary: null,
    latest: null,
    fuelEvents: [],
    dailyMileage: [],
    dailyFuelConsumption: [],
    journal: null,
    dailyMovement: [],

    dashboardLoading: false,
    latestLoading: false,
    error: null,
    stale: false,
};

// ── Slice ───────────────────────────────────────────────────────────────────

export const MachineryTelemetrySlice = createSlice({
    name: "machinery_telemetry",
    initialState,
    reducers: {
        /** Полная очистка (смена машины) — данные обнуляются + stale */
        clearTelemetry: () => ({...initialState, stale: true}),
        /** Пометить данные устаревшими (смена дат) — данные остаются, но карточки затемняются */
        markStale: (state) => {
            state.stale = true;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── Dashboard (единый запрос за период) ──
            .addCase(fetchTelemetryDashboard.pending, (state) => {
                state.dashboardLoading = true;
                state.stale = false;
                state.error = null;
            })
            .addCase(
                fetchTelemetryDashboard.fulfilled,
                (state, action: PayloadAction<ITelemetryDashboard>) => {
                    const d = action.payload;
                    state.summary = d.summary;
                    state.fuelHistory = d.fuelHistory;
                    state.voltageHistory = d.voltageHistory;
                    state.engineHistory = d.engineHistory;
                    state.fuelEvents = d.fuelEvents;
                    state.dailyMileage = d.dailyMileage;
                    state.dailyFuelConsumption = d.dailyFuelConsumption;
                    state.journal = d.journal;
                    state.dailyMovement = d.dailyMovement;
                    state.dashboardLoading = false;
                },
            )
            // ── Latest ──
            .addCase(fetchLatestTelemetry.pending, (state) => {
                state.latestLoading = true;
            })
            .addCase(
                fetchLatestTelemetry.fulfilled,
                (state, action: PayloadAction<ITelemetryPoint | null>) => {
                    state.latest = action.payload;
                    state.latestLoading = false;
                },
            )
            .addCase(fetchTelemetryDashboard.rejected, (state, action) => {
                state.dashboardLoading = false;
                if (action.meta.aborted) return;
                if (action.payload === "billing_restricted") return;
                state.error = typeof action.payload === "string"
                    ? action.payload
                    : "Ошибка загрузки телеметрии";
            })
    },
});

export const {clearTelemetry, markStale} = MachineryTelemetrySlice.actions;
export default MachineryTelemetrySlice.reducer;